import { match } from 'ts-pattern';
import { SupportedPlatforms } from '~/constants/platforms';
import { CreatorBenchmarks } from '~/types';
import { formatAbbr, formatPercentage } from '~/utils';

import { MetricCardProps } from './MetricCard';

export interface PlatformMetrics {
  title: MetricCardProps['metricTitle'];
  subtitle?: MetricCardProps['metricSubtitle'];
  value: MetricCardProps['metricValue'];
  transformValue: MetricCardProps['metricTransformValue'];
  isDisabled?: boolean;
}

export type ChannelMetrics = Record<SupportedPlatforms, PlatformMetrics[]>;
export type PlatformMetricsSource = Record<
  SupportedPlatforms,
  string | undefined
>;
export const getPlatformMetrics = (
  benchmarks: CreatorBenchmarks,
): ChannelMetrics => {
  return {
    instagram: [
      {
        title: 'Average Reach',
        subtitle: 'Last 30 days - Reels, Stories, Posts',
        value: benchmarks.igAvgFeedReelsStoryReach,
        transformValue: 'number',
      },
      {
        title: 'Average Views',
        // Fallback to old metric if new is not available (GraphAPI v22 Deprecated Impressions)
        subtitle: 'Last 30 days - Reels, Stories, Posts',
        value:
          benchmarks.igAvgFeedStoryViews ||
          benchmarks.igAvgFeedStoryImpressions,
        transformValue: 'number',
      },
      {
        title: 'Average Engagements',
        subtitle: 'Last 30 days - Reels, Posts',
        value: benchmarks.igAvgFeedReelsEngagements,
        transformValue: 'number',
      },
      {
        title: 'Follower Engagement Rate',
        subtitle: 'Last 30 days - Reels, Posts',
        value: benchmarks.igAvgFeedReelsFollowerEngRate,
        transformValue: 'percentage',
      },
      {
        title: 'Reach Engagement Rate',
        subtitle: 'Last 30 days - Reels, Posts',
        value: benchmarks.igAvgFeedReelsReachEngRate,
        transformValue: 'percentage',
      },
      {
        title: 'View Engagement Rate',
        subtitle: 'Last 30 days - Reels, Posts',
        value: benchmarks.igAvgReelsViewsEngRate,
        transformValue: 'percentage',
      },
      {
        title: 'Average Reels Views',
        subtitle: 'Last 30 days - Reels',
        value: benchmarks.igAvgReelsViews,
        transformValue: 'number',
      },
      {
        title: 'Average Story Reach',
        subtitle: 'Last 30 days - Stories',
        value: benchmarks.igAvgStoryReach,
        transformValue: 'number',
      },
      {
        title: 'Average Story Views',
        subtitle: 'Last 30 days - Stories',
        // Fallback to old metric if new is not available (GraphAPI v22 Deprecated Impressions)
        value: benchmarks.igAvgStoryViews || benchmarks.igAvgStoryImpressions,
        transformValue: 'number',
      },
    ],
    tiktok: [
      {
        title: 'Average Views',
        subtitle: 'Last 30 videos',
        value: benchmarks.ttAvgViews,
        transformValue: 'number',
      },
      {
        title: 'Average Engagements',
        subtitle: 'Last 30 videos',
        value: benchmarks.ttAvgEngagements,
        transformValue: 'number',
      },
      {
        title: 'Average Likes',
        subtitle: 'Last 30 videos',
        value: benchmarks.ttAvgLikes,
        transformValue: 'number',
      },
      {
        title: 'Average Comments ',
        subtitle: 'Last 30 videos',
        value: benchmarks.ttAvgComments,
        transformValue: 'number',
      },
      {
        title: 'Average Shares',
        subtitle: 'Last 30 videos',
        value: benchmarks.ttAvgShares,
        transformValue: 'number',
      },
      {
        title: 'View Engagement Rate',
        subtitle: 'Last 30 videos',
        value: benchmarks.ttAvgViewsEngRate,
        transformValue: 'percentage',
      },
    ],
    youtube: [
      {
        title: 'Average Views',
        subtitle: 'Last 28 days',
        value: benchmarks.ytAvgViews,
        transformValue: 'number',
      },
      {
        title: 'Average Likes',
        subtitle: 'Last 28 days',
        value: benchmarks.ytAvgLikes,
        transformValue: 'number',
      },
      {
        title: 'Average Comments',
        subtitle: 'Last 28 days',
        value: benchmarks.ytAvgComments,
        transformValue: 'number',
      },
      {
        title: 'Average Shares',
        subtitle: 'Last 28 days',
        value: benchmarks.ytAvgShares,
        transformValue: 'number',
      },
    ],
    snapchat: [
      {
        title: 'Profile Tier',
        subtitle: 'Last 28 days',
        value: benchmarks.snpProfileTier,
        transformValue: 'string',
      },
      {
        title: 'Story Avg View Time',
        value: benchmarks.snpStoryAvgViewTimeMs,
        transformValue: 'number',
        subtitle: 'Last 28 days',
      },
      {
        title: 'Saved Story Avg View Time',
        value: benchmarks.snpSavedStoryAvgViewTimeMs,
        transformValue: 'number',
        subtitle: 'Last 28 days',
      },
      {
        title: 'Spotlight Avg View Time',
        value: benchmarks.snpSpotlightAvgViewTimeMs,
        transformValue: 'number',
        subtitle: 'Last 28 days',
      },
    ],
  };
};

export const getMetricsSource = (
  benchmarks: CreatorBenchmarks,
): PlatformMetricsSource => {
  return {
    instagram: benchmarks.igBenchmarksSource,
    tiktok: benchmarks.ttBenchmarksSource,
    youtube: benchmarks.ytBenchmarksSource,
    snapchat: undefined, // TODO: Add snapchat metrics source when available
  };
};

export const getReadableMetricValue = (
  metricValue: MetricCardProps['metricValue'],
  metricTransformValue: MetricCardProps['metricTransformValue'],
) => {
  return match(metricTransformValue)
    .with('number', () => formatAbbr(Number(metricValue) as number))
    .with('percentage', () => formatPercentage(metricValue as number, 'n/a', 1))
    .otherwise(() => metricValue);
};

export const isZeroOrUndefined = (value: string | number | undefined) => {
  if (value === undefined) return true;

  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return parsed === 0;
};
